import { inject, Injectable } from '@angular/core';

import { DatadogLogger } from '../datadog/datadog-logger';

/**
 * The native SDKs inject the V2 interface: the global itself is callable with an operation name.
 * The V1 named methods (`FS.identify`, `FS.event`, …) are a compatibility layer the same injected
 * snippet hangs off that callable, so they are never present without it and never reach anything
 * the callable cannot.
 */
type FullStoryApi = (operation: string, options?: unknown) => unknown;

/** Everything the app says to the interface the native FullStory SDKs inject into the WebView. */
@Injectable({ providedIn: 'root' })
export class FullStoryService {
  private readonly logger = inject(DatadogLogger);

  /**
   * Read on every call, never cached: the native SDK re-injects its shim on each WebView page
   * load, so a reference captured at construction goes stale at the first navigation.
   */
  private get fs(): FullStoryApi | undefined {
    return typeof window === 'undefined' ? undefined : (window as { FS?: FullStoryApi }).FS;
  }

  grantConsent(): boolean {
    return this.call('setIdentity', { consent: true });
  }

  identify(uid: string, properties: Record<string, unknown>): boolean {
    return this.call('setIdentity', { uid, properties });
  }

  trackEvent(name: string, properties: Record<string, unknown>): boolean {
    return this.call('trackEvent', { name, properties });
  }

  observeSessionStart(callback: () => void): boolean {
    return this.call('observe', { type: 'start', callback });
  }

  getCurrentSessionUrl(): string | undefined {
    const fs = this.fs;
    if (!fs) return undefined;

    try {
      const url = fs('getSession', { format: 'url' });
      return typeof url === 'string' ? url : undefined;
    } catch (error) {
      this.logger.error({ message: '[FullStory] getSession failed', error });
      return undefined;
    }
  }

  async getSessionIdAsync(): Promise<string | undefined> {
    const fs = this.fs;
    if (!fs) return undefined;

    try {
      const id = await (fs('getSessionAsync', { format: 'id' }) as Promise<unknown>);
      return typeof id === 'string' ? id : undefined;
    } catch (error) {
      this.logger.error({ message: '[FullStory] getSessionAsync failed', error });
      return undefined;
    }
  }

  /** Reports whether the call reached an interface at all — the operations return nothing. */
  private call(operation: string, options: unknown): boolean {
    const fs = this.fs;
    if (!fs) return false;

    try {
      fs(operation, options);
      return true;
    } catch (error) {
      this.logger.error({ message: `[FullStory] ${operation} failed`, error });
      return false;
    }
  }
}
